<script setup lang="ts">
// Extend Window interface for Plausible
declare global {
  interface Window {
    plausible?: (event: string, options?: any) => void
  }
}

interface Props {
  maxVisibleButtons?: number
  totalPages: number
  total: number
  perPage: number
  currentPage: number
}

const props = withDefaults(defineProps<Props>(), {
  maxVisibleButtons: 5,
})

const emit = defineEmits<{
  pagechanged: [page: number]
}>()

const startPage = computed(() => {
  if (props.currentPage === 1)
    return 1
  if (props.totalPages < props.maxVisibleButtons)
    return 1
  if (props.currentPage === props.totalPages)
    return props.totalPages - props.maxVisibleButtons + 1
  return props.currentPage - 1
})

const endPage = computed(() => {
  return Math.min(
    startPage.value + props.maxVisibleButtons - 1,
    props.totalPages,
  )
})

const pages = computed(() => {
  const range = []
  for (let i = startPage.value; i <= endPage.value; i += 1) {
    range.push({
      name: i,
      isDisabled: i === props.currentPage,
    })
  }
  return range
})

const isInFirstPage = computed(() => props.currentPage === 1)
const isInLastPage = computed(() => props.currentPage === props.totalPages)

function onClickFirstPage() {
  emit('pagechanged', 1)
  // Track pageview for Plausible analytics
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible('pageview')
  }
}
function onClickPreviousPage() {
  emit('pagechanged', props.currentPage - 1)
  // Track pageview for Plausible analytics
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible('pageview')
  }
}
function onClickPage(page: number) {
  emit('pagechanged', page)
  // Track pageview for Plausible analytics
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible('pageview')
  }
}
function onClickNextPage() {
  emit('pagechanged', props.currentPage + 1)
  // Track pageview for Plausible analytics
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible('pageview')
  }
}
function onClickLastPage() {
  emit('pagechanged', props.totalPages)
  // Track pageview for Plausible analytics
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible('pageview')
  }
}
const isPageActive = (page: number) => props.currentPage === page
</script>

<template>
  <div class="flex items-center justify-between mt-5 mb-2 sm:px-0 gap-2 w-full">
    <!-- Boutons de navigation à gauche -->
    <div class="flex gap-2">
      <UButton
        :disabled="isInFirstPage"
        variant="soft"
        size="xl"
        color="secondary"
        icon="i-mdi-arrow-collapse-left"
        aria-label="Aller à la première page"
        @click="onClickFirstPage"
      />
      <UButton
        :disabled="isInFirstPage"
        variant="soft"
        size="xl"
        color="secondary"
        icon="i-mdi-arrow-left"
        @click="onClickPreviousPage"
      >
        <span class="hidden ml-1 lg:inline w-18">Précédent</span>
      </UButton>
    </div>

    <!-- Numéros de page au centre -->
    <div class="flex gap-2">
      <template v-for="page in pages" :key="page.name">
        <UButton
          v-if="isPageActive(page.name)"
          color="secondary"
          size="xl"
          class="flex"
          variant="solid"
        >
          {{ page.name }}
        </UButton>
        <UButton
          v-else
          variant="soft"
          size="xl"
          color="secondary"
          class="hidden md:flex"
          @click="onClickPage(page.name)"
        >
          {{ page.name }}
        </UButton>
      </template>
    </div>

    <!-- Boutons de navigation à droite -->
    <div class="flex gap-2">
      <UButton
        :disabled="isInLastPage"
        variant="soft"
        size="xl"
        color="secondary"
        trailing-icon="i-mdi-arrow-right"
        @click="onClickNextPage"
      >
        <span class="hidden mr-1 lg:inline w-18">Suivant</span>
      </UButton>
      <UButton
        :disabled="isInLastPage"
        variant="soft"
        size="xl"
        color="secondary"
        trailing-icon="i-mdi-arrow-collapse-right"
        @click="onClickLastPage"
      />
    </div>
  </div>

  <!-- Indicateur de page -->
  <div class="text-center text-sm text-gray-400 mt-0">
    Page {{ currentPage }} de {{ totalPages }}
  </div>
</template>
